/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import type { Dispatch } from '@reduxjs/toolkit';

import { CartActions, SecurityHelper } from 'sarsaparilla';
import type { CsrAccount } from 'sarsaparilla/auth';

import { isFirstPartyUri } from 'shared-ui/utils/redirectUri';

import { getCsrCustomerSession, postCsrSession } from '../api/account';
import type { TypedDispatch } from '../redux';
import {
    csrCustomerCleared,
    csrSessionWritten,
    loggedOut,
    sessionWritten,
} from './auth.slice';
import type { AuthSession } from './auth.slice';

function isAuthSession(value: unknown): value is AuthSession {
    return (
        typeof value === 'object' &&
        value !== null &&
        'access_token' in value &&
        'expiration' in value
    );
}

function isCsrAccount(value: unknown): value is CsrAccount {
    return (
        typeof value === 'object' &&
        value !== null &&
        'access_token' in value &&
        'expiration' in value
    );
}

function pushGtmUserId(userId: string | null): void {
    if (window.dataLayer) {
        window.dataLayer.push({
            userId,
        });
    }
}

/**
 * Write the visitor/customer session to localStorage (`recaccount`) and mirror
 * it into Redux. localStorage remains the source of truth; Redux is the
 * reactive projection.
 *
 * Guarded, silent-failure by design. Most callers are untyped JS, so the
 * `AuthSession` type is not enforced at this boundary. If handed anything that
 * is not a session — a `{success: true}` "verification email sent" body, an
 * `undefined` payload, `null` — this logs to `console.error` and returns
 * without writing. It deliberately does **not** throw: several callers
 * (e.g. the `saveChangesToAccount` account-update thunks) are not wrapped in
 * try/catch, so throwing here would tear down the update flow and surface as an
 * unhandled error — the very "bricked auth" failure this guard exists to
 * prevent. No-op-and-log keeps `recaccount` intact instead.
 *
 * This guard is a last-resort backstop, not input validation callers may lean
 * on. Because the failure is silent, a caller MUST NOT treat "I called
 * writeSession" as "a session was written": branch on the response shape first
 * and only call this with an actual session. The reference pattern lives in
 * `ui-account/src/actions/profileActions.js`:
 *
 * ```js
 * if ('success' in response.data) {
 *     dispatch(emailSent());        // ack only — do NOT write a session
 * } else {
 *     writeSession(dispatch, profile);
 *     dispatch(profileUpdated());   // success signal gated behind the real write
 * }
 * ```
 *
 * Keeping the success/"updated" dispatch inside the `else` is what stops the
 * silent no-op from lying to the rest of the app about a write that never
 * happened.
 */
export function writeSession(dispatch: Dispatch, profile: AuthSession): void {
    if (!isAuthSession(profile)) {
        // eslint-disable-next-line no-console
        console.error('writeSession: refusing to persist a non-session payload');
        return;
    }

    window.localStorage.setItem('recaccount', JSON.stringify(profile));
    dispatch(sessionWritten(profile));
}

/**
 * Restore Redux from the stored session. Reads still go through sarsaparilla
 * security helpers (token refresh lives there until a later phase).
 */
export function hydrateFromStorage(dispatch: Dispatch): void {
    if (SecurityHelper.isLoggedIn()) {
        const recAccount = SecurityHelper.getRecAccount();
        if (isAuthSession(recAccount)) {
            dispatch(sessionWritten(recAccount));
        }
    }

    csrLoggedIn(dispatch);
}

/**
 * Nuclear sign-out: wipe all web storage, clear the Redux session (visitor and
 * CSR), and hard-redirect to the widget home URL or `/`.
 */
export function logout(dispatch: Dispatch): void {
    window.localStorage.clear();
    window.sessionStorage.clear();
    dispatch(loggedOut());
    window.location.assign(SecurityHelper.getWidgetHomeUrl() ?? '/');
}

/**
 * Hydrate `csrAccount` from storage on bootstrap. Reads still go through
 * sarsaparilla security helpers until the helper/refresh phase.
 */
export function csrLoggedIn(dispatch: Dispatch): void {
    if (SecurityHelper.isExternalCSRLoggedIn()) {
        const csrAccount = SecurityHelper.getExternalCSRAccount();
        if (isCsrAccount(csrAccount)) {
            dispatch(csrSessionWritten(csrAccount));
        }
    }
}

/**
 * Drop the impersonated customer session without ending the CSR session.
 * Removes `recaccount`, clears GTM `userId`, and nulls `account`.
 */
export function csrClearSelectedCustomer(dispatch: Dispatch): void {
    window.localStorage.removeItem('recaccount');
    pushGtmUserId(null);
    dispatch(csrCustomerCleared());
}

/**
 * Where to land when a CSR redirect target is unusable.
 *
 * Normally that is the widget home URL, so a CSR working inside an embedded
 * widget stays in it. But `SecurityHelper.getWidgetHomeUrl()` derives that URL
 * from `window.location.href` and caches it in `sessionStorage.widgetUrl` the
 * first time it runs in a widget session — and it re-derives it whenever the
 * current href carries `src=widget`. On the csr-log-in hop that means it can
 * hand back the csr-log-in URL itself, and assigning that would reload the page,
 * re-run the login effect, reject the same target again and re-POST the
 * single-use CSR token, forever. Fall through to `/` whenever the widget home is
 * the page we are already on.
 *
 * @param entryHref - The csr-log-in href, captured before the session request.
 * Not read from `window.location` here: the caller replaces the URL while that
 * request is in flight, so by the time this runs the live href is already the
 * post-login one and would never match the page we are trying to detect.
 */
function csrFallbackUrl(entryHref: string): string {
    const widgetHome = SecurityHelper.getWidgetHomeUrl();
    if (!widgetHome) {
        return '/';
    }

    try {
        const resolved = new URL(widgetHome, entryHref);
        return resolved.href === entryHref ? '/' : widgetHome;
    } catch {
        return '/';
    }
}

async function writeCsrSession(dispatch: Dispatch, csrToken: string): Promise<void> {
    const profile = await postCsrSession(csrToken);
    window.localStorage.setItem('csraccount', JSON.stringify(profile));
    dispatch(csrSessionWritten(profile));
    csrClearSelectedCustomer(dispatch);
}

/**
 * Establish the CSR session (`csraccount` + `csrAccount`) and clear any
 * selected customer.
 */
export async function csrSession(dispatch: Dispatch, csrToken: string): Promise<void> {
    try {
        await writeCsrSession(dispatch, csrToken);
    } catch {
        // HTTP failed before writeCsrSession. Leave recaccount and account as
        // they were so a failed Hub login does not desync Redux from storage.
    }
}

/**
 * Same as `csrSession`, then navigate to `newUrl`.
 *
 * `newUrl` arrives from a query parameter on the Hub side (see
 * `ui-csr/src/components/CSRRedirect.jsx`), so it is attacker-reachable: left
 * unchecked it is a `javascript:` sink executing on our own origin with a fresh
 * impersonation token in localStorage, and an open redirect off one of our
 * URLs. Anything that is not a first-party target falls back to
 * {@link csrFallbackUrl}. The check deliberately happens after the session is
 * written, so a rejected target does not cost the CSR their session.
 */
export async function csrSessionWithUrl(
    dispatch: Dispatch,
    csrToken: string,
    newUrl: string
): Promise<void> {
    // Read before the await. `CsrLogInPage` calls this un-awaited and then
    // navigates with `replace: true`, so the href changes under us mid-request.
    const entryHref = window.location.href;

    try {
        await writeCsrSession(dispatch, csrToken);
        window.location.assign(
            isFirstPartyUri(newUrl) ? newUrl : csrFallbackUrl(entryHref)
        );
    } catch {
        // HTTP failed before writeCsrSession. Leave recaccount and account as
        // they were so a failed Hub login does not desync Redux from storage.
    }
}

/**
 * Impersonate a customer: load their session into `recaccount` / `account`,
 * push GTM `userId`, and refetch the cart through sarsaparilla. Redirects
 * home if a customer was already selected.
 */
export async function csrSelectCustomer(
    dispatch: TypedDispatch,
    accountId: string
): Promise<void> {
    try {
        const profile = await getCsrCustomerSession(accountId);
        const userCurrentlySelected = window.localStorage.getItem('recaccount') !== null;
        writeSession(dispatch, profile);
        pushGtmUserId(profile.account.account_id);
        dispatch(CartActions.fetchCart());
        if (userCurrentlySelected) {
            window.location.assign(SecurityHelper.getWidgetHomeUrl() ?? '/');
        }
    } catch {
        // HTTP failed before writeSession. Leave recaccount and account as
        // they were so a failed switch does not desync Redux from storage.
    }
}

// cSpell:ignore recaccount, csraccount
